import React, { useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import PageHeader from "./pageHeader/PageHeader";
import useFAST from "../../contexts/FASTContext";

import type { JSX, ReactNode } from "react";

import type { Breadcrumbs } from "../../types";

interface IFASTLayout {
  children: ReactNode;
}

function FASTLayout({ children }: IFASTLayout): JSX.Element {
  const layoutBodyRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<{
    scrollHeight: number;
    clientHeight: number;
  } | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const history = useHistory<{
    lastPath?: {
      name: string;
      url?: string;
    };
  }>();

  const { t } = useTranslation("editor-fast-channel");
  const location = useLocation();
  const { state } = useFAST();

  const { channelData } = state;

  const channelName = channelData?.name;

  const locationPath = location.pathname.split("/");

  const getLastPath = useCallback((): {
    name: string;
    url?: string;
  } => {
    const lastPath = history.location.state?.lastPath;

    if (lastPath) {
      return lastPath;
    }

    return { name: t(locationPath[locationPath.length - 1] as string) };
  }, [history.location.state, locationPath, t]);

  const pageBreadcrumbs = state.layout?.header?.breadcrumbs;

  const getBreadcrumbs = useCallback((): Breadcrumbs => {
    if (!pageBreadcrumbs) {
      const breadcrumbsBase = [
        {
          name: t("fastChannels"),
          url: "/fast-channels",
        },
        {
          name: channelName ?? "...",
          url: `/fast-channels/${channelData?.uuid}/distribute`,
        },
      ];

      const lastPath = getLastPath();
      return [...breadcrumbsBase, lastPath];
    }

    return pageBreadcrumbs;
  }, [pageBreadcrumbs, t, channelData?.uuid, channelName, getLastPath]);

  const handleScroll = useCallback((event: Event): void => {
    if (event.target instanceof Element) {
      const { target } = event;

      const scrollTop = target?.scrollTop;

      scrollRef.current ??= {
        scrollHeight: target.scrollHeight,
        clientHeight: target.clientHeight,
      };

      if (
        scrollRef.current &&
        scrollRef.current.scrollHeight - scrollRef.current.clientHeight > 50 &&
        scrollTop > 0
      ) {
        setIsScrolling(true);
        return;
      }

      setIsScrolling(false);
    }
  }, []);

  const handleResize = useCallback((): void => {
    const layoutBodyRefCurrent = layoutBodyRef.current;

    if (layoutBodyRefCurrent) {
      scrollRef.current = {
        scrollHeight: layoutBodyRefCurrent.scrollHeight,
        clientHeight: layoutBodyRefCurrent.clientHeight,
      };
    }
  }, []);

  useEffect(() => {
    setIsScrolling(false);

    const layoutBodyRefCurrent = layoutBodyRef.current;

    if (layoutBodyRefCurrent) {
      layoutBodyRefCurrent.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (layoutBodyRefCurrent) {
        layoutBodyRefCurrent.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
      }
    };
  }, [handleScroll, handleResize]);

  return (
    <div
      className="bg-background relative flex flex-auto flex-col overflow-hidden"
      id="fast-layout-container"
    >
      <PageHeader
        breadcrumbs={getBreadcrumbs()}
        isScrolling={isScrolling}
        logoUrl={channelData?.logo_url}
      />
      <div
        ref={layoutBodyRef}
        className="flex h-full flex-row overflow-auto p-[0.78125rem]"
      >
        {children}
      </div>
    </div>
  );
}

export default FASTLayout;
